<template>
  <div class="container-fluid p-4">
    <div class="row">
      <div class="col-12">
        <div class="box mt-2">
          <div class="row">
            <h3>{{ $t('export.statisticData.title') }}</h3>
            <p>{{ $t('export.statisticData.description') }}</p>
          </div>

          <!-- Form -->
          <div class="row mt-4">
            <div class="col-12">
              <div class="mb-4">
                <label class="form-label">
                  {{ $t('export.statisticData.periodLabel') }}
                  <span class="d-inline-block" tabindex="0" data-bs-toggle="popover" data-bs-trigger="hover focus" :data-bs-content="$t('export.statisticData.tooltips.dateRange')">
                    <i class="bi bi-question-circle ms-2 help-icon"></i>
                  </span>
                </label>
                <DateRangePicker v-model="dateRange" alignment="left" :max-days="100" :min-date="minDate" :max-date="maxDate" />
              </div>

              <div class="mb-4">
                <label class="form-label">{{ $t('export.statisticData.dataTypeLabel') }}</label>
                <select v-model="selectedDataType" class="form-select form-select-width" :class="{ 'mobile': isMobile }">
                  <option value="statistic_summaries">{{ $t('export.statisticData.dataTypes.statistic_summaries') }}</option>
                  <option value="statistic_visitor_attributes">{{ $t('export.statisticData.dataTypes.statistic_visitor_attributes') }}</option>
                  <option value="statistic_visitor_accommodation_bookings">{{ $t('export.statisticData.dataTypes.statistic_visitor_accommodation_bookings') }}</option>
                  <option value="statistic_visitor_event_bookings">{{ $t('export.statisticData.dataTypes.statistic_visitor_event_bookings') }}</option>
                </select>
              </div>

              <div class="mb-4">
                <label class="form-label">
                  {{ $t('export.statisticData.encodingLabel') }}
                  <span class="d-inline-block" tabindex="0" data-bs-toggle="popover" data-bs-trigger="hover focus" :data-bs-content="$t('export.statisticData.tooltips.encodingType')">
                    <i class="bi bi-question-circle ms-2 help-icon"></i>
                  </span>
                </label>
                <RadioButton
                  v-model="selectedEncoding"
                  :options="encodingOptions"
                  name="encoding"
                />
              </div>
            </div>
          </div>

          <!-- Export Button -->
          <div class="row mt-4">
            <div class="col-12 text-center">
              <button
                type="button"
                class="btn btn-primary btn-lg"
                @click="handleExport"
                :disabled="!selectedDataType || !selectedEncoding || isExporting"
              >
                <span v-if="isExporting">
                  <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {{ $t('export.statisticData.exporting') }}
                </span>
                <span v-else>
                  {{ $t('export.statisticData.exportButton') }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Dialog -->
    <ErrorDialog
      v-model="showErrorDialog"
      :message="errorDialogMessage"
      @confirm="closeErrorDialog"
    />
</div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { Popover } from 'bootstrap';
import DateRangePicker from '@/components/user/dashboard/DateRangePicker.vue';
import ErrorDialog from '@/components/common/ErrorDialog.vue';
import RadioButton from '@/components/common/RadioButton.vue';
import { useAuthUser } from '@/composables/useAuthUser';
import { fetchDateRangeLimits } from '@/utils/statisticDataUtils.js';
import { useIsMobile } from '@/utils/DeviceUtil';
import { colors } from '../../../../consts/colors';

const { t: $t } = useI18n();
const { currentUser } = useAuthUser();
const { isMobile } = useIsMobile();

// Calculate default date range (today to 30 days later) in JST
const getJSTDate = (daysOffset = 0) => {
  const now = new Date();
  // Convert to JST (UTC+9)
  const jstOffset = 9 * 60; // JST is UTC+9 hours in minutes
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const jstDate = new Date(utc + (jstOffset * 60000));
  jstDate.setHours(0, 0, 0, 0);
  jstDate.setDate(jstDate.getDate() + daysOffset);
  return jstDate;
};

const today = getJSTDate(0);
const thirtyDaysLater = getJSTDate(30);

// Form state
const dateRange = ref({
  startDate: today,
  endDate: thirtyDaysLater
});
const selectedDataType = ref('statistic_summaries');
const selectedEncoding = ref('sjis');

// Encoding options
const encodingOptions = computed(() => [
  { value: 'sjis', label: $t('export.statisticData.encodings.sjis') },
  { value: 'utf8', label: $t('export.statisticData.encodings.utf8') }
]);

// Export state
const isExporting = ref(false);

// Error dialog state
const showErrorDialog = ref(false);
const errorDialogMessage = ref('');

// Date range limits
const minDate = ref(null);
const maxDate = ref(null);

// Load date range limits
const loadDateRangeLimits = async () => {
  const { minDate: min, maxDate: max } = await fetchDateRangeLimits();
  minDate.value = min;
  maxDate.value = max;
};

// Initialize on mount
onMounted(() => {
  const popoverTriggerList = document.querySelectorAll('[data-bs-toggle="popover"]');
  [...popoverTriggerList].map(popoverTriggerEl => new Popover(popoverTriggerEl, {
    customClass: 'custom-popover'
  }));
  loadDateRangeLimits();
});

// Methods
const formatDateForApi = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const handleExport = async () => {
  // Parameter blank validation
  if (!selectedDataType.value) {
    showError($t('export.statisticData.errors.select_data_type'));
    return;
  }

  if (!selectedEncoding.value) {
    showError($t('export.statisticData.errors.select_encoding'));
    return;
  }

  // Validate date range (max 100 days)
  const daysDiff = Math.floor(
    (dateRange.value.endDate - dateRange.value.startDate) / (1000 * 60 * 60 * 24)
  );

  if (daysDiff > 100) {
    showError($t('export.statisticData.errors.max_days'));
    return;
  }

  if (daysDiff < 0) {
    showError($t('export.statisticData.errors.invalid_date_range'));
    return;
  }

  // Check if user has VIEW permission
  const hasViewPermission = currentUser.value?.permissions?.some(
    p => p.permissionType === 'VIEW'
  );

  if (!hasViewPermission) {
    showError($t('export.statisticData.errors.no_permission'));
    return;
  }

  try {
    isExporting.value = true;

    const startDate = formatDateForApi(dateRange.value.startDate);
    const endDate = formatDateForApi(dateRange.value.endDate);

    const response = await axios.get('/api/user/export/statistic-data/export', {
      params: {
        data_type: selectedDataType.value,
        start_date: startDate,
        end_date: endDate,
        encoding: selectedEncoding.value
      },
      responseType: 'blob'
    });

    // Create download link
    const blob = new Blob([response.data], { type: 'text/csv' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;

    // Generate filename with timestamp
    const now = new Date();
    const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
    const dataTypeName = selectedDataType.value;
    link.download = `${dataTypeName}_${timestamp}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (err) {
    console.error('Error exporting CSV:', err);

    if (err.response?.status === 403) {
      showError($t('export.statisticData.errors.no_permission'));
    } else if (err.response?.status === 422) {
      // Validation error from backend
      // Since responseType is 'blob', we need to parse the blob as JSON
      try {
        const errorText = await err.response.data.text();
        const errorData = JSON.parse(errorText);
        const message = errorData.message || $t('export.statisticData.errors.invalid_data');
        showError(message);
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
        showError($t('export.statisticData.errors.invalid_data'));
      }
    } else {
      showError($t('export.statisticData.errors.export_failed'));
    }
  } finally {
    isExporting.value = false;
  }
};

const showError = (message) => {
  errorDialogMessage.value = message;
  showErrorDialog.value = true;
};

const closeErrorDialog = () => {
  showErrorDialog.value = false;
  errorDialogMessage.value = '';
};
</script>

<style lang="scss" scoped>
.box {
  background-color: v-bind('colors.white');
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 1px 3px v-bind('colors.shadowColor');
}

.form-select-width {
  max-width: 400px;
  width: auto;

  &.mobile {
    width: 100%;
    justify-content: space-between;
  }
}

.btn-primary {
  background-color: v-bind('colors.blue[500]');
  border-color: v-bind('colors.blue[500]');
  min-width: 200px;
  padding: 0.75rem 2rem;

  &:hover:not(:disabled) {
    background-color: v-bind('colors.blue[600]');
    border-color: v-bind('colors.blue[600]');
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
